import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { UserMenu } from '@/components/layout/user-menu';
import { useMe } from '@/hooks/use-me';
import { useAuthStore } from '@shared/stores/auth-store';
import { getTokenStore } from '@shared/api/token-store';
import { logout } from '@shared/api/auth';
import { NotificationsBell } from '@/components/layout/notifications-bell';
import type { AdminRole } from '@shared/types/api';

const ADMIN_ROLE_LABELS: Record<AdminRole, string> = {
  SUPER_ADMIN: 'Super Admin',
  OH_SERVICE_MANAGER: 'OH / Service Manager',
  ASSIGNER: 'Assigner',
  CASE_EXECUTIVE: 'Case Executive',
  ASSOCIATE_CONTROLLING_EXECUTIVE: 'Associate Controlling Executive',
  CUSTOMER_SUPPORT_EXECUTIVE: 'Customer Support Executive',
  FINANCE_EXECUTIVE: 'Finance Executive',
  AUDITOR_COMPLIANCE_REVIEWER: 'Auditor / Compliance Reviewer',
  OPS_ADMIN: 'Ops Admin',
  READ_ONLY_ADMIN: 'Read Only Admin',
  ASSIGNER_ADMIN: 'Assigner Admin',
};

export function AdminTopbar({ onMenuClick }: { onMenuClick: () => void }) {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const signOut = useAuthStore((s) => s.signOut);
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  async function handleLogout() {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch {
      /* ignore */
    }
    getTokenStore().clear('admin');
    qc.clear();
    signOut();
    setIsLoggingOut(false);
    setConfirmOpen(false);
    navigate('/login', { replace: true });
  }

  return (
    <>
      <header className="sticky top-0 z-30 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between gap-4 px-4 py-3 md:px-6">
          <div className="flex items-center gap-2">
            <button
              onClick={onMenuClick}
              className="rounded p-1 text-gray-500 hover:bg-gray-100 md:hidden"
              aria-label="Open menu"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <NotificationsBell />
            <UserMenu
              name={[me?.firstName, me?.lastName].filter(Boolean).join(' ') || me?.email || 'Admin'}
              email={me?.email ?? ''}
              role={me?.adminRole ? (ADMIN_ROLE_LABELS[me.adminRole] ?? me.adminRole) : undefined}
              onSignOut={() => setConfirmOpen(true)}
            />
          </div>
        </div>
      </header>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-600 bg-opacity-50 backdrop-blur-sm">
          <div className="w-full max-w-sm transform rounded-lg bg-white p-6 shadow-xl transition-all">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600 mb-4">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </div>
            <h3 className="text-center text-lg font-semibold leading-6 text-gray-900">Sign out</h3>
            <p className="mt-2 text-center text-sm text-gray-500">
              Are you sure you want to sign out?
            </p>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={isLoggingOut}
                className="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
              >
                No
              </button>
              <button
                type="button"
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="flex-1 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 focus:outline-none disabled:opacity-50"
              >
                {isLoggingOut ? 'Signing out...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


